import * as React from 'react';

import { Label } from '@/shared/components/ui/label';
import { cn } from '@/shared/lib/cn';

export interface FormRowProps {
  /** Must match the control's `id` so clicking the label focuses it. */
  htmlFor?: string;
  label: React.ReactNode;
  /** Helper text shown under the label; hidden once an error is displayed. */
  description?: React.ReactNode;
  error?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * The single field layout used by every form: label, control, and exactly one
 * message slot underneath. Wiring `aria-describedby`/`aria-invalid` here means
 * no individual form can forget to make its errors accessible.
 */
export function FormRow({
  htmlFor,
  label,
  description,
  error,
  required,
  className,
  children,
}: FormRowProps) {
  const messageId = htmlFor ? `${htmlFor}-message` : undefined;

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={htmlFor}>
        {label}
        {required ? (
          <span className="text-destructive" aria-hidden>
            *
          </span>
        ) : null}
      </Label>

      {children}

      {error ? (
        <p id={messageId} role="alert" className="text-destructive text-xs leading-relaxed">
          {error}
        </p>
      ) : description ? (
        <p id={messageId} className="text-muted-foreground text-xs leading-relaxed">
          {description}
        </p>
      ) : null}
    </div>
  );
}

/** Props every control in a `FormRow` needs for accessibility. */
export function fieldA11yProps(id: string, error?: string) {
  return {
    id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': `${id}-message`,
  } as const;
}
